import { ExpandableText, type ExpandableTextProps } from '@components/molecules/ExpandableText'

import type { ComponentDoc, PropDocs } from '../types'

const ADDRESS = 'New Farm Park Ferry Terminal - Hawthorne Ferry Terminal, Hawthorne, QLD, 4005, Australia'

const props: PropDocs<ExpandableTextProps> = {
  text: { type: 'string', required: true, description: 'The secondary text to show on one line.' },
  expandLabel: {
    type: 'string',
    required: true,
    description: 'Accessible name for the … button, saying what it reveals ("Show full address").',
  },
}

const doc: ComponentDoc<ExpandableTextProps> = {
  slug: 'expandable-text',
  name: 'ExpandableText',
  tier: 'molecule',
  description:
    'One line of grey secondary text that truncates to fit its container. When it is cut off, a … button shows the rest, and a Show less button folds it back.',
  importStatement: "import { ExpandableText } from '@components/molecules/ExpandableText'",
  sourcePath: 'app/frontend/components/molecules/ExpandableText.tsx',
  props,
  examples: [
    {
      title: 'In a table cell',
      note: 'The cell caps the width. Short text shows in full with no button.',
      render: () => (
        <div className="tw:flex tw:max-w-80 tw:flex-col tw:gap-4">
          <ExpandableText text={ADDRESS} expandLabel="Show full address" />
          <ExpandableText text="Virtual Event" expandLabel="Show full address" />
        </div>
      ),
      code: `// column: meta: { allowWrap: true }
<div className="tw:flex tw:max-w-80 tw:flex-col">
  <a href={routes.editEvent}>{name}</a>
  <ExpandableText text={fullVenueAddress} expandLabel={t('event_row.show_full_address')} />
</div>`,
    },
  ],
  usage: {
    do: [
      'Cap its container, like tw:max-w-80 on the cell content. Truncation alone does not stop a table column growing to fit the text.',
      'In a DataTable, set meta: { allowWrap: true } on the column, so the row title beside it can wrap inside the cap.',
      'Use it for a supporting line under a row title, like an address or a note, so long values do not make the table scroll.',
    ],
    dont: [
      'Use it for the row title or anything the user must read to act. Let that wrap.',
      'Use it for several lines of text. It only truncates one.',
      'Use it where hovering is enough to read the rest. That is TruncationTooltip; this one works by tap and keyboard too.',
    ],
  },
  a11y: [
    'The full text is always in the DOM, so screen readers read all of it.',
    'The toggle is one button that stays focused as it switches between … (named by expandLabel) and Show less, and it reports aria-expanded. CallingSessionSupportersDataTable styles its row from that attribute.',
  ],
}

export default doc
